import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { dayjs } from '../../libs/day';
import { DATE_PICKER_DATE_BUTTON_TEST_ID } from './constants';
import { EDatePickerModifiedDate, ICalendayDay } from './types';
import { createCalendar, getFormattedWeekDays } from './utils';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const WrapperSpan = styled.span`
  ${tw`flex items-center justify-center w-full text-p-small-m leading-p-small-m lg:text-p-xsmall leading-p-xsmall`}
  text-transform: capitalize;
  min-height: 42px;
  width: 100%;
`;

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isStartDate: boolean;
  isEndDate: boolean;
  isBetween: boolean;

  hasEndDate: boolean;
  hasStartDate: boolean;
}

const Button = styled(WrapperSpan)<ButtonProps>`
  ${tw`cursor-pointer flex flex-col py-small px-0 overflow-hidden
    hover:bg-grey-200 hover:rounded-15 
  `}

  ${({ isEndDate, isStartDate }) =>
    (isStartDate || isEndDate) &&
    tw`
    text-white bg-primary-400
    hover:rounded-none hover:bg-primary-400
    `}

  ${({ hasEndDate, isStartDate }) =>
    isStartDate &&
    css`
      ${hasEndDate
        ? tw`
            rounded-tl-15 rounded-bl-15
            hover:rounded-tl-15 hover:rounded-bl-15
      `
        : tw`rounded-15 hover:rounded-15`}
    `}

  ${({ hasStartDate, isEndDate }) =>
    isEndDate &&
    css`
      ${hasStartDate
        ? tw`
            rounded-tr-15 rounded-br-15  
            hover:rounded-tr-15 hover:rounded-br-15`
        : tw`rounded-15
             hover:rounded-15
            `}
    `}

  ${({ isBetween, isEndDate, isStartDate }) =>
    isBetween &&
    !isStartDate &&
    !isEndDate &&
    tw`
    bg-grey-200 text-primary-400 
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      ${tw`text-grey-300 cursor-not-allowed`}
    `}
`;

const Container = styled(Wrapper)`
  ${tw`text-p-medium-m leading-p-medium-m overflow-x-auto lg:text-p-small lg:leading-p-small`}
  row-gap: 4px;
  white-space: nowrap;
`;

export interface DatePickerCalendarProps {
  currentMonthOfDate: dayjs.Dayjs;

  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;

  modifiedDate: EDatePickerModifiedDate;

  isOutOfRange?: (date: dayjs.Dayjs) => boolean;
  onStartDateChange: (date: dayjs.Dayjs) => void;
  onEndDateChange: (date: dayjs.Dayjs) => void;
  onUpdateModifiedDate: (type: EDatePickerModifiedDate) => void;
  renderDayExtraContent?: (props: {
    isDisabled: boolean;
    day: dayjs.Dayjs;
  }) => React.ReactNode;
}

const DatePickerCalendar: React.FC<DatePickerCalendarProps> = ({
  currentMonthOfDate,
  endDate,
  isOutOfRange,
  modifiedDate,
  onEndDateChange,
  onStartDateChange,
  onUpdateModifiedDate,
  renderDayExtraContent,
  startDate,
}) => {
  const [hoveredDate, setHoveredDate] = useState<dayjs.Dayjs | null>(null);

  const WEEK_DAYS: string[] = getFormattedWeekDays();

  const CALENDAR: ICalendayDay[] = createCalendar(currentMonthOfDate);

  function onMouseEnter(date: dayjs.Dayjs) {
    if (
      modifiedDate !== EDatePickerModifiedDate.END ||
      !!endDate ||
      (startDate && date.isBefore(startDate))
    ) {
      return;
    }

    setHoveredDate(date);
  }

  function onMouseLeave() {
    setHoveredDate(null);
  }

  function updateStartDate(date) {
    if (endDate && dayjs(endDate).isBefore(date)) {
      onEndDateChange(null);
      onUpdateModifiedDate(EDatePickerModifiedDate.END);
    }

    onStartDateChange(date);
  }

  function updateEndDate(date) {
    if (startDate && dayjs(startDate).isAfter(date)) {
      onStartDateChange(date);
      onEndDateChange(null);
    } else {
      onEndDateChange(date);
    }
  }

  function isBetween(date) {
    if (!hoveredDate && !startDate && !endDate) {
      return false;
    }

    if (modifiedDate === EDatePickerModifiedDate.END && !endDate) {
      return date.isBetween(startDate, hoveredDate);
    }

    return date.isBetween(endDate, startDate);
  }

  return (
    <Container>
      {WEEK_DAYS.map((weekDay, index) => (
        <WrapperSpan key={index}>{weekDay}</WrapperSpan>
      ))}
      {CALENDAR.map((day, index) => {
        const formattedDate = day.date.format('YYYY-MM-DD');

        if (!day.isCurrentMonth) {
          return <span key={index} data-date={formattedDate}></span>;
        }

        let isEndDate: boolean = endDate
          ? endDate.isSame(day.date, 'day')
          : false;

        if (!isEndDate && hoveredDate) {
          isEndDate = hoveredDate.isSame(day.date, 'day');
        }

        const isStartDate = startDate
          ? startDate.isSame(day.date, 'day')
          : false;

        const isBetweenStartAndEndDate = isBetween(day.date);
        const isDisabled = isOutOfRange?.(day.date) || false;

        return (
          <Button
            key={index}
            as="button"
            data-between={isBetweenStartAndEndDate}
            data-date={formattedDate}
            data-enddate={isEndDate}
            data-startdate={isStartDate}
            data-testid={`${DATE_PICKER_DATE_BUTTON_TEST_ID}-${day.dayOfMonth}`}
            disabled={isDisabled}
            hasEndDate={!!endDate || !!hoveredDate}
            hasStartDate={!!startDate}
            isBetween={isBetweenStartAndEndDate}
            isEndDate={isEndDate}
            isStartDate={isStartDate}
            onClick={() => {
              if (modifiedDate === EDatePickerModifiedDate.START) {
                updateStartDate(day.date);
              } else if (modifiedDate === EDatePickerModifiedDate.END) {
                updateEndDate(day.date);
              }
            }}
            onMouseEnter={() => onMouseEnter(day.date)}
            onMouseLeave={onMouseLeave}
          >
            {day.dayOfMonth}
            {renderDayExtraContent?.({ isDisabled, day: day.date })}
          </Button>
        );
      })}
    </Container>
  );
};

export default DatePickerCalendar;
